import { Tooltip } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import PasswordConfirmationDialog from '../../molecules/PasswordConfirmationDialog';
import * as St from './PrintEstimationButtons.styles';

import { LOCAL_STORAGE_KEY } from '~/core/constants';
import { getSearchParameter } from '~/core/helpers';
import useEstimateSaver from '~/core/hooks/useEstimateSaver';
import { useRetailerSettings } from '~/core/hooks/useRetailerSettings';
import { UserOption } from '~/core/models';
import { postDownloadPdf } from '~/core/services/estimate-pdf-service';
import Print from '~/icons/Print.svg';
import PrintWarning from '~/icons/PrintWarning.svg';
import { useEstimateContext } from '~/legacy/easy-estimator-page/context/estimateContext';

const PrintEstimationButtons = () => {
  const [askPasswordConfirmation, setAskPasswordConfirmation] = useState(false);

  const { t } = useTranslation();

  const { fetchedEstimate } = useEstimateContext();

  const { retailerSettings } = useRetailerSettings();

  const { isEstimateDirty } = useEstimateSaver();

  const userToken = getSearchParameter(LOCAL_STORAGE_KEY.USER_TOKEN);

  const isEstimateSaved = Boolean(fetchedEstimate);

  const hasInternalCostPrint =
    retailerSettings?.userOptions?.includes(UserOption.PrintInternal) &&
    !!userToken;

  const handlePrintEstimate = async ({
    isInternalCost,
    password,
  }: {
    isInternalCost?: boolean;
    password?: string;
  }): Promise<boolean> => {
    if (!fetchedEstimate) {
      return false;
    }

    const { code } = fetchedEstimate;

    try {
      await postDownloadPdf({
        code,
        filename: `${fetchedEstimate?.estimateNumber}${isInternalCost ? '-internal-cost' : ''}.pdf`,
        isInternalCost,
        password,
        userToken,
      });

      return true;
    } catch (error) {
      return false;
    }
  };

  const onPasswordConfirm = (password: string) => {
    handlePrintEstimate({ isInternalCost: true, password }).then((result) => {
      if (result) {
        setAskPasswordConfirmation(false);
      }
    });
  };

  return (
    <St.Container>
      {isEstimateSaved && !isEstimateDirty && (
        <>
          <Tooltip title={t('ORDER_SUMMARY.PRINT.RETAILER_COST_TOOLTIP')}>
            <span>
              <St.Icon
                disabled={!fetchedEstimate?.code}
                onClick={() => handlePrintEstimate({ isInternalCost: false })}
                component={() => <Print />}
              />
            </span>
          </Tooltip>

          {hasInternalCostPrint && (
            <Tooltip title={t('ORDER_SUMMARY.PRINT.INTERNAL_COST_TOOLTIP')}>
              <span>
                <St.Icon
                  onClick={() => setAskPasswordConfirmation(true)}
                  component={() => <PrintWarning />}
                />
              </span>
            </Tooltip>
          )}
        </>
      )}

      <PasswordConfirmationDialog
        isVisible={askPasswordConfirmation}
        onConfirm={(value) => onPasswordConfirm(value)}
        onCancel={() => {
          setAskPasswordConfirmation(false);
        }}
      />
    </St.Container>
  );
};

export default PrintEstimationButtons;
